@if (selectedItem) {
  <div class="overview-layout space-y-6 sm:space-y-8">
    <section class="project-intro" aria-labelledby="project-heading">
      <div class="project-story">
        <p class="section-eyebrow">PROPERTY OVERVIEW</p>
        <h2 id="project-heading">About the project</h2>
        <div id="project-description" class="project-description" [class.line-clamp-3]="!isDescriptionExpanded">
          {{ selectedItem.description || 'Project description not available.' }}
        </div>
        @if (selectedItem.description?.length > 280) {
          <button type="button" class="description-toggle" (click)="toggleDescription()" [attr.aria-expanded]="isDescriptionExpanded" aria-controls="project-description">
            {{ isDescriptionExpanded ? 'Read less' : 'Read full description' }}
            <svg aria-hidden="true" viewBox="0 0 24 24" [class.is-expanded]="isDescriptionExpanded"><path d="m6 9 6 6 6-6" /></svg>
          </button>
        }
      </div>
      @if (selectedItem.communityCategory || selectedItem.sellerProfile) {
        <aside class="community-panel" aria-label="Community and seller">
          <span class="community-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2m20 0v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/><circle cx="9" cy="7" r="4"/></svg></span>
          <dl>
            @if (selectedItem.communityCategory) {
              <div><dt>Community category</dt><dd>{{ selectedItem.communityCategory }}</dd></div>
            }
            @if (selectedItem.sellerProfile) {
              <div><dt>Seller profile</dt><dd>{{ selectedItem.sellerProfile }}</dd></div>
            }
          </dl>
        </aside>
      }
    </section>
    @if (selectedItem.livingEnvironment?.length) {
      <section class="living-environment" aria-labelledby="environment-heading">
        <h3 id="environment-heading" class="section-eyebrow">LIVING ENVIRONMENT</h3>
        <ul class="environment-list">
          @for (environment of selectedItem.livingEnvironment; track environment) {
            <li><svg aria-hidden="true" viewBox="0 0 24 24"><path d="m5 12 4 4L19 6"/></svg><span>{{ environment }}</span></li>
          }
        </ul>
      </section>
    }
    <section class="property-specifications" aria-labelledby="specifications-heading">
      <div class="specifications-heading"><div><p class="section-eyebrow">THE DETAILS</p><h2 id="specifications-heading">Property specifications</h2></div></div>
      <div class="specification-groups">
        <div class="specification-group"><h3>Space &amp; interiors</h3><dl>
          <div class="spec-row"><dt>Super Built-up Area</dt><dd>@if (selectedItem.superBuiltUpArea) { {{ selectedItem.superBuiltUpArea | numberFormat }} <span class="unit">sqft</span> } @else { -- }</dd></div>
          <div class="spec-row"><dt>Land Area</dt><dd>@if (selectedItem.landArea) { {{ selectedItem.landArea | numberFormat }} <span class="unit">sqft</span> } @else { -- }</dd></div>
          <div class="spec-row"><dt>Facing</dt><dd>{{ selectedItem.facing || '--' }}</dd></div>
          <div class="spec-row"><dt>Furnishing</dt><dd>{{ selectedItem.furnishing || '--' }}</dd></div>
        </dl></div>
        <div class="specification-group"><h3>Building &amp; layout</h3><dl>
          <div class="spec-row">
            <dt>Property Type</dt>
            <dd>
              {{ selectedItem.propertyType || '--' }}
              @if (propertyStructureTypes.length) {
                <span class="spec-note">{{ propertyStructureTypes.join(' · ') }}</span>
              }
            </dd>
          </div>
          <div class="spec-row"><dt>Structure</dt><dd>{{ formatBuildingStr(selectedItem.structure) }}</dd></div>
          <div class="spec-row"><dt>Age of Property</dt><dd>{{ selectedItem.ageOfProperty || '--' }}</dd></div>
          <div class="spec-row"><dt>Parking Availability</dt><dd>{{ selectedItem.parking || '--' }} {{ selectedItem.parkingCapacity || '' }}</dd></div>
        </dl></div>
        <div class="specification-group"><h3>Ownership &amp; pricing</h3><dl>
          <div class="spec-row"><dt>Possession Status</dt><dd>{{ selectedItem.possession || '--' }}</dd></div>
          <div class="spec-row"><dt>Possession Year</dt><dd>{{ selectedItem.possessionYear || '--' }}</dd></div>
          <div class="spec-row"><dt>Avg. Price</dt><dd>@if (selectedItem.price?.length) { ₹{{ selectedItem.price[0].averagePrice | numberFormat }}<span class="unit">/sqft</span> } @else { -- }</dd></div>
          <div class="spec-row"><dt>RERA</dt><dd>{{ selectedItem.reraNumber || 'TBA' }}</dd></div>
        </dl></div>
      </div>
    </section>

    <!-- =========================================================
         ADDITIONAL ROOMS
    ========================================================== -->
    <section
      class="border-t border-slate-200
             pt-6 sm:pt-8"
    >

      <div class="mb-4">
        <h2
          class="text-base sm:text-lg
                 font-semibold
                 text-slate-900"
        >
          Additional Rooms
        </h2>
      </div>

      @if (selectedItem.additionalRooms?.length) {

        <div class="flex flex-wrap gap-2">

          @for (
            room of selectedItem.additionalRooms;
            track room
          ) {

            <div
              class="inline-flex items-center gap-2
                     rounded-lg
                     border border-slate-200
                     bg-white
                     px-3 py-2
                     text-xs sm:text-sm
                     text-slate-700"
            >

              <i
                [class]="getRoomIcon(room)"
                class="text-[#5B4FD4]"
              ></i>

              <span>
                {{ room }}
              </span>

            </div>

          }

        </div>

      } @else {

        <div
          class="flex items-center
                 gap-2
                 text-sm
                 text-slate-400"
        >
          <span
            class="flex h-6 w-6
                   items-center justify-center
                   rounded-full
                   bg-slate-100"
          >
            ✓
          </span>

          None
        </div>

      }

    </section>


    <!-- =========================================================
         AMENITIES
    ========================================================== -->
    <section
      class="border-t border-slate-200
             pt-6 sm:pt-8"
    >

      <div class="mb-4">
        <h2
          class="text-base sm:text-lg
                 font-semibold
                 text-slate-900"
        >
          Amenities
        </h2>
      </div>


      <div class="flex flex-wrap gap-2">

        @for (
          amenity of getVisibleAmenities(selectedItem.amenities);
          track amenity.key
        ) {

          <div
            class="inline-flex items-center gap-2
                   rounded-full
                   border border-slate-200
                   bg-white
                   px-3 py-2
                   text-xs sm:text-sm
                   text-slate-700
                   transition-colors
                   hover:border-[#5B4FD4]/30
                   hover:bg-[#5B4FD4]/5"
          >

            <svg
              class="h-4 w-4
                     shrink-0
                     text-[#5B4FD4]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                [attr.d]="amenity.icon"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>

            <span>
              {{ amenity.label }}
            </span>

          </div>

        } @empty {

          <p class="text-sm text-slate-400">
            No amenities available.
          </p>

        }

      </div>

    </section>


    <!-- =========================================================
         CIRCLED INTELLIGENCE (CI) PREVIEW
         Conversion block for users without Intelligence access.
    ========================================================== -->
    @if (!auth.canAccessPropertyFeature('intelligence') && !propertyAccessGranted) {
    <section
      class="relative overflow-hidden
             rounded-2xl
             border border-[#8C52FF]/20
             bg-gradient-to-br
             from-[#FAF7FF]
             via-white
             to-[#F4F0FF]
             p-4 sm:p-5 lg:p-6"
    >

      <!-- Decorative glow -->
      <div
        class="pointer-events-none absolute -right-16 -top-16
               h-36 w-36 rounded-full
               bg-[#8C52FF]/10 blur-3xl"
      ></div>

      <div class="relative">

        <!-- Header -->
        <div class="flex items-start justify-between gap-4">

          <div class="min-w-0">

            <div
              class="mb-2 inline-flex items-center gap-1.5
                     text-[10px] sm:text-[11px]
                     font-semibold uppercase
                     tracking-[0.07em]
                     text-[#7040E8]"
            >

              <span
                class="flex h-6 w-6 shrink-0
                       items-center justify-center
                       rounded-full
                       bg-[#8C52FF]/10"
              >
                <svg
                  class="h-3.5 w-3.5 text-[#8C52FF]"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                >
                  <path
                    d="M12 3l2.5 5.5L20 11l-5.5 2.5L12 19l-2.5-5.5L4 11l5.5-2.5L12 3z"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>

              Circled Intelligence (CI) Preview

            </div>

            <h2
              class="max-w-2xl
                     text-xl sm:text-2xl lg:text-[26px]
                     font-semibold
                     leading-tight
                     tracking-tight
                     text-slate-900"
            >
              Know the value before you make an offer.
            </h2>

            <p
              class="mt-2 max-w-2xl
                     text-xs sm:text-sm
                     leading-5 sm:leading-6
                     text-slate-500"
            >
              Get the complete analysis behind this property,
              including transaction evidence, fair value,
              land value, rental potential and negotiation guidance.
            </p>

          </div>

          <span
            class="shrink-0
                   rounded-full
                   bg-[#8C52FF]/10
                   px-3 py-1.5
                   text-[10px] sm:text-xs
                   font-semibold
                   text-[#7040E8]"
          >
            Preview
          </span>

        </div>


        <!-- CI capabilities -->
        <div
          class="mt-5
                 intelligence-features grid
                 gap-2"
        >

          <!-- Transaction Data -->
          <div
            class="flex items-center gap-2.5
                   rounded-xl
                   border border-[#8C52FF]/10
                   bg-white/80
                   px-3 py-2.5"
          >
            <span
              class="flex h-7 w-7 shrink-0
                     items-center justify-center
                     rounded-lg
                     bg-[#8C52FF]/10"
            >
              <svg
                class="h-3.5 w-3.5 text-[#7040E8]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
              >
                <ellipse cx="12" cy="5" rx="7" ry="3"></ellipse>
                <path d="M5 5v7c0 1.7 3.1 3 7 3s7-1.3 7-3V5"></path>
                <path d="M5 12v7c0 1.7 3.1 3 7 3s7-1.3 7-3v-7"></path>
              </svg>
            </span>

            <span
              class="text-xs sm:text-sm
                     font-medium
                     text-slate-700"
            >
              Transaction Data
            </span>
          </div>


          <!-- Fair Value -->
          <div
            class="flex items-center gap-2.5
                   rounded-xl
                   border border-[#8C52FF]/10
                   bg-white/80
                   px-3 py-2.5"
          >
            <span
              class="flex h-7 w-7 shrink-0
                     items-center justify-center
                     rounded-lg
                     bg-[#8C52FF]/10"
            >
              <svg
                class="h-3.5 w-3.5 text-[#7040E8]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
              >
                <path
                  d="M12 3v18M17 7H9.5a3.5 3.5 0 0 0 0 7H15a3.5 3.5 0 0 1 0 7H7"
                  stroke-linecap="round"
                />
              </svg>
            </span>

            <span
              class="text-xs sm:text-sm
                     font-medium
                     text-slate-700"
            >
              Fair Value &amp; Asking Price Comparison
            </span>
          </div>


          <!-- UDS -->
          <div
            class="flex items-center gap-2.5
                   rounded-xl
                   border border-[#8C52FF]/10
                   bg-white/80
                   px-3 py-2.5"
          >
            <span
              class="flex h-7 w-7 shrink-0
                     items-center justify-center
                     rounded-lg
                     bg-[#8C52FF]/10"
            >
              <svg
                class="h-3.5 w-3.5 text-[#7040E8]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
              >
                <path
                  d="M4 19V5h16v14M4 9h16M9 5v14"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>

            <span
              class="text-xs sm:text-sm
                     font-medium
                     text-slate-700"
            >
              {{ isLandProperty ? 'Land Value Assessment' : 'UDS & Land Value Analysis' }}
            </span>
          </div>


          <!-- Rental -->
          <div
            class="flex items-center gap-2.5
                   rounded-xl
                   border border-[#8C52FF]/10
                   bg-white/80
                   px-3 py-2.5"
          >
            <span
              class="flex h-7 w-7 shrink-0
                     items-center justify-center
                     rounded-lg
                     bg-[#8C52FF]/10"
            >
              <svg
                class="h-3.5 w-3.5 text-[#7040E8]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
              >
                <path
                  d="M4 19V5M4 19h16"
                  stroke-linecap="round"
                />
                <path
                  d="M7 15l4-4 3 2 5-6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>

            <span
              class="text-xs sm:text-sm
                     font-medium
                     text-slate-700"
            >
              Rental Yield &amp; Income Potential
            </span>
          </div>


          <!-- Negotiation -->
          <div
            class="flex items-center gap-2.5
                   rounded-xl
                   border border-[#8C52FF]/10
                   bg-white/80
                   px-3 py-2.5
                   intelligence-feature-wide"
          >
            <span
              class="flex h-7 w-7 shrink-0
                     items-center justify-center
                     rounded-lg
                     bg-[#8C52FF]/10"
            >
              <svg
                class="h-3.5 w-3.5 text-[#7040E8]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
              >
                <path
                  d="M8 12h8M8 8h8M8 16h5"
                  stroke-linecap="round"
                />
                <rect
                  x="4"
                  y="3"
                  width="16"
                  height="18"
                  rx="2"
                />
              </svg>
            </span>

            <span
              class="text-xs sm:text-sm
                     font-medium
                     text-slate-700"
            >
              Suggested Offer &amp; Negotiation Guidance
            </span>
          </div>

        </div>


        <!-- CTA -->
        <div
          class="intelligence-cta mt-5
                 flex flex-col
                 gap-3"
        >

          <p
            class="text-[10px] sm:text-xs
                   text-slate-400"
          >
            Make a more informed property decision with Circled Intelligence (CI).
          </p>

          <button
            type="button"
            (click)="openIntelligencePlans()"
            class="inline-flex
                   min-h-[44px]
                   shrink-0
                   items-center
                   justify-center
                   gap-2
                   rounded-xl
                   bg-gradient-to-r
                   from-[#7040E8]
                   to-[#8C52FF]
                   px-5
                   text-xs sm:text-sm
                   font-semibold
                   text-white
                   shadow-[0_8px_22px_rgba(140,82,255,0.24)]
                   transition-all
                   hover:-translate-y-0.5
                   hover:shadow-[0_11px_28px_rgba(140,82,255,0.30)]
                   active:translate-y-0"
          >
            Unlock Circled Intelligence (CI)

            <svg
              class="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                d="M5 12h14M13 6l6 6-6 6"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>

        </div>

      </div>

    </section>
    }
  </div>
}
